import React from 'react';
import { Form } from 'react-bootstrap';
import {
    PROGRAM_NAMES,
    formatReleaseMoment,
    formatWindowDate,
} from '@/shared/utils/deadline';

// Close date for one program: the caller's extension or the program's
// window from /term/current, else the stored window (a term loaded by code
// has no applicationWindows), same fallback as applicationWindowService.
function programCloseDate(term, key) {
    const verdict = term?.applicationWindows?.[key];
    return (
        verdict?.extensionCloseDate ||
        verdict?.closeDate ||
        term?.programWindows?.[key]?.applicationCloseDate ||
        term?.applicationCloseDate ||
        null
    );
}

function programDatesText(term, key) {
    const close = programCloseDate(term, key);
    const decisions = term?.programWindows?.[key]?.decisionsReleased;
    const closeText = close
        ? `applications close ${formatWindowDate(close)}`
        : 'application close date not announced yet';
    const decisionsText = decisions
        ? `decisions released ${formatReleaseMoment(decisions)}`
        : 'decision date not announced yet';
    return `${closeText}; ${decisionsText}.`;
}

// Single source of truth for "what's missing from General Acknowledgments,"
// shared by CreateApplication.js's and ApplicationDetailPage.js's
// canProceed/error-summary — same pattern as
// CreateApplicationSection4.js's getCourseContextErrors. idPrefix must match
// whatever the caller passes to the component below, since that's what the
// checkbox's DOM id is built from.
export function getGeneralAcknowledgmentErrors(
    acknowledgesGeneralTerms,
    idPrefix
) {
    if (acknowledgesGeneralTerms !== true) {
        return [
            {
                id: `${idPrefix}-acknowledges-general-terms`,
                label: 'General acknowledgments',
            },
        ];
    }
    return [];
}

// Boolean form of getGeneralAcknowledgmentErrors, for callers that only
// need "is this complete," not the error detail — same pattern as
// isCourseContextComplete/isProgramQuestionnaireComplete, including having
// no production caller today (kept for CreateApplication.test.js's
// dedicated unit tests). idPrefix is irrelevant to the boolean result (it
// only affects the id string inside an error entry), so this doesn't take
// one.
export function isGeneralAcknowledgmentComplete(acknowledgesGeneralTerms) {
    return (
        getGeneralAcknowledgmentErrors(acknowledgesGeneralTerms, 'ca')
            .length === 0
    );
}

// Section 6: General Acknowledgments (Qualtrics Block 6) — applies to every
// applicant regardless of which program(s) they selected, unlike Section3's
// LA-specific contract acknowledgment.
//
// idPrefix keeps the checkbox's DOM id unique per caller (create wizard vs
// detail page), same reasoning as LAContractAcknowledgment's idPrefix, in
// case both ever end up in the same tree.
// programs is the ranked selection (each has a key), so the dates shown
// are only for the programs this person asked for.
function CreateApplicationSection6({
    acknowledgesGeneralTerms,
    onChange,
    term,
    programs = [],
    idPrefix,
    errorIds = new Set(),
}) {
    const checkboxId = `${idPrefix}-acknowledges-general-terms`;
    const errorTextId = `${checkboxId}-error`;
    const isInvalid = errorIds.has(checkboxId);

    return (
        <div>
            <h2 className="h5 mb-3">General Acknowledgments</h2>
            <p className="text-muted mb-3">
                By submitting this application, I also understand that:
            </p>
            <ul className="mb-3">
                <li>
                    Requesting multiple programs does not guarantee support from
                    any of them — each program reviews and decides
                    independently, on its own timeline.
                </li>
                {programs.length > 0 && (
                    <li>
                        Key dates for {term?.name || 'this term'}:
                        <ul>
                            {programs.map((program) => (
                                <li key={program.key}>
                                    <span className="fw-semibold">
                                        {PROGRAM_NAMES[program.key] ||
                                            program.key.toUpperCase()}
                                        :
                                    </span>{' '}
                                    {programDatesText(term, program.key)}
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
            </ul>
            <Form.Check
                type="checkbox"
                id={checkboxId}
                label={
                    <>
                        I acknowledge the above.{' '}
                        <span className="text-danger">*</span>
                    </>
                }
                checked={acknowledgesGeneralTerms === true}
                onChange={(e) => onChange(e.target.checked)}
                required
                isInvalid={isInvalid}
                aria-invalid={isInvalid}
                aria-describedby={isInvalid ? errorTextId : undefined}
            />
            {isInvalid && (
                <Form.Control.Feedback
                    type="invalid"
                    id={errorTextId}
                    role="alert"
                    className="d-block"
                >
                    Acknowledgment is required.
                </Form.Control.Feedback>
            )}
        </div>
    );
}

export default CreateApplicationSection6;
